import React, { useState } from 'react';
import { Modal, Button, Select, InputNumber, Input, Upload, Icon, Spin, Alert, DatePicker } from 'antd';
import moment from 'moment';
import api from 'api';

const Option = Select.Option;

const NEW_PAYMENT = 'new';

const TYPE_OPTIONS = [
    { value: 'check',       label: 'Check' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'ach',         label: 'ACH' },
    { value: 'wire',        label: 'Wire' },
];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function errorText(errors) {
    if(!errors) return 'Could not save the payment.';
    if(typeof errors === 'string') return errors;
    if(Array.isArray(errors)) return errors.map(e => e.message || e).join(', ');
    return errors.message || 'Could not save the payment.';
}

// Custom "Add a Payment" modal for the invoice screen. Unlike the declarative reference form it
// (a) fetches the invoice customer's open transactions and lets the user apply existing credit,
// (b) separates the money received (Payment Amount -> transaction) from the Amount to Apply to
// this invoice (invoice_payments.amount), and (c) switches instrument fields on payment type.
// It reuses LinkedEditor.handleSubmit (passed as `onCreate`) which builds the multipart body,
// injects invoice_id, and reloads on success.
export default function AddPaymentModal(props) {
    const { values, onCreate, createLabel, disabled } = props;
    const invoice = values || {};
    const invoiceOpenBalance = Number(invoice.open_balance || 0);

    const [visible, setVisible] = useState(false);
    const [loadingTx, setLoadingTx] = useState(false);
    const [openTransactions, setOpenTransactions] = useState([]);

    const [source, setSource] = useState(NEW_PAYMENT);   // NEW_PAYMENT or an existing transaction id
    const [paymentDate, setPaymentDate] = useState(moment());   // application's payment_date; post-dating allowed
    const [type, setType] = useState('check');
    const [paymentAmount, setPaymentAmount] = useState(null);
    const [applyAmount, setApplyAmount] = useState(null);
    const [checkNumber, setCheckNumber] = useState('');
    const [lastDigits, setLastDigits] = useState('');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [memo, setMemo] = useState('');
    const [file, setFile] = useState(null);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const reset = () => {
        setSource(NEW_PAYMENT);
        setPaymentDate(moment());
        setType('check');
        setPaymentAmount(null);
        setApplyAmount(invoiceOpenBalance > 0 ? invoiceOpenBalance : null);
        setCheckNumber(''); setLastDigits(''); setReferenceNumber('');
        setMemo(''); setFile(null); setError(null); setSubmitting(false);
    };

    const openModal = async () => {
        reset();
        setVisible(true);
        if(invoice.customer_id) {
            setLoadingTx(true);
            const res = await api.get('/lists/customer_transactions', { customer_id: invoice.customer_id });
            const rows = (res && res.body) || [];
            setOpenTransactions(rows.filter(t => Number(t.available_credit) > 0));
            setLoadingTx(false);
        }
    };

    const selectSource = (val) => {
        setSource(val);
        setError(null);
        if(val === NEW_PAYMENT) {
            setApplyAmount(invoiceOpenBalance > 0 ? invoiceOpenBalance : null);
        } else {
            const tx = openTransactions.find(t => t.id === val);
            const credit = tx ? Number(tx.available_credit) : 0;
            // default to the transaction's open balance, capped at what the invoice still owes
            setApplyAmount(invoiceOpenBalance > 0 ? Math.min(credit, invoiceOpenBalance) : credit);
        }
    };

    const submit = async () => {
        setError(null);
        const apply = Number(applyAmount);
        if(!apply || apply <= 0) { setError('Enter an amount to apply to this invoice.'); return; }

        let payload = { amount: apply };
        // The application's own payment_date (may differ from the transaction's / today). Post-dating
        // is allowed. Sent as YYYY-MM-DD so it lands as a plain calendar date, tz-agnostic.
        payload.payment_date = (paymentDate || moment()).format('YYYY-MM-DD');
        if(memo) payload.memo = memo;

        if(source !== NEW_PAYMENT) {
            const tx = openTransactions.find(t => t.id === source);
            if(tx && apply > Number(tx.available_credit)) {
                setError(`Amount to apply exceeds this payment's available credit (${money(tx.available_credit)}).`);
                return;
            }
            payload.transaction_id = source;
        } else {
            const pay = Number(paymentAmount);
            if(!pay || pay <= 0) { setError('Enter the payment amount.'); return; }
            if(apply > pay) { setError('Amount to apply cannot exceed the payment amount.'); return; }
            payload.payment_amount = pay;
            payload.type = type;
            if(type === 'check') payload.check_number = checkNumber;
            else if(type === 'credit_card') payload.last_digits = lastDigits;
            else if(type === 'ach' || type === 'wire') payload.reference_number = referenceNumber;
            if(file) payload.file = file;
        }

        setSubmitting(true);
        try {
            const res = await onCreate(payload);   // builds FormData, injects invoice_id, reloads on success
            if(res && res.errors) { setError(errorText(res.errors)); setSubmitting(false); return; }
            // success path reloads the page (reloadPageOnCreate); modal unmounts.
        } catch(e) {
            setError('Could not save the payment.');
            setSubmitting(false);
        }
    };

    const isNew = source === NEW_PAYMENT;
    const selectedTx = isNew ? null : openTransactions.find(t => t.id === source);

    const label = { display: 'block', fontWeight: 600, margin: '0 0 4px' };
    const row = { marginBottom: 14 };
    const fullWidth = { width: '100%' };

    return <React.Fragment>
        <Button style={{marginRight: '12px'}} icon='plus' disabled={disabled} onClick={openModal}>{createLabel || 'Add a Payment'}</Button>
        <Modal
            title={createLabel || 'Add a Payment'}
            visible={visible}
            onCancel={() => setVisible(false)}
            confirmLoading={submitting}
            okText='Save Payment'
            onOk={submit}
            destroyOnClose>

            {error ? <Alert type='error' message={error} style={{marginBottom: 14}} /> : null}

            <p style={{marginBottom: 14}}>Applying to invoice <b>{invoice.invoice_number}</b>
                {invoiceOpenBalance > 0 ? <span> — open balance <b>{money(invoiceOpenBalance)}</b></span> : null}</p>

            {loadingTx
                ? <div style={{textAlign: 'center', padding: '12px'}}><Spin /></div>
                : (openTransactions.length > 0
                    ? <div style={row}>
                        <span style={label}>Payment</span>
                        <Select style={fullWidth} value={source} onChange={selectSource}>
                            <Option value={NEW_PAYMENT}>New payment</Option>
                            {openTransactions.map(t =>
                                <Option key={t.id} value={t.id}>
                                    {t.transaction_number} — available {money(t.available_credit)}{t.memo ? ` (${t.memo})` : ''}
                                </Option>)}
                        </Select>
                      </div>
                    : null)}

            {isNew
                ? <React.Fragment>
                    <div style={row}>
                        <span style={label}>Payment Type</span>
                        <Select style={fullWidth} value={type} onChange={setType}>
                            {TYPE_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                        </Select>
                    </div>

                    {type === 'check' ? <div style={row}>
                        <span style={label}>Check #</span>
                        <Input value={checkNumber} onChange={e => setCheckNumber(e.target.value)} />
                    </div> : null}

                    {type === 'credit_card' ? <div style={row}>
                        <span style={label}>Last 4 CC Digits</span>
                        <Input value={lastDigits} onChange={e => setLastDigits(e.target.value)} maxLength={4} />
                    </div> : null}

                    {(type === 'ach' || type === 'wire') ? <div style={row}>
                        <span style={label}>Confirmation #</span>
                        <Input value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} />
                    </div> : null}

                    <div style={row}>
                        <span style={label}>Payment Amount</span>
                        <InputNumber style={fullWidth} min={0} step={0.01} precision={2}
                            value={paymentAmount}
                            formatter={v => `$ ${v}`}
                            parser={v => v.replace(/\$\s?/g, '')}
                            onChange={setPaymentAmount} />
                    </div>

                    <div style={row}>
                        <span style={label}>Attachment</span>
                        <Upload beforeUpload={f => { setFile(f); return false; }}
                            onRemove={() => setFile(null)}
                            fileList={file ? [file] : []}>
                            <Button><Icon type='upload' /> Select File</Button>
                        </Upload>
                    </div>
                  </React.Fragment>
                : (selectedTx ? <div style={row}>
                    <span style={label}>Selected Payment</span>
                    <div>{selectedTx.transaction_number} — available credit {money(selectedTx.available_credit)}</div>
                  </div> : null)}

            <div style={row}>
                <span style={label}>Payment Date</span>
                <DatePicker style={fullWidth} value={paymentDate} onChange={d => setPaymentDate(d)}
                    format='MMM DD, YYYY' allowClear={false} />
            </div>

            <div style={row}>
                <span style={label}>Amount to Apply to Invoice</span>
                <InputNumber style={fullWidth} min={0} step={0.01} precision={2}
                    value={applyAmount}
                    formatter={v => `$ ${v}`}
                    parser={v => v.replace(/\$\s?/g, '')}
                    onChange={setApplyAmount} />
            </div>

            <div style={row}>
                <span style={label}>Memo</span>
                <Input value={memo} onChange={e => setMemo(e.target.value)} />
            </div>
        </Modal>
    </React.Fragment>;
}
